import { useState } from "react";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Sparkle } from "@phosphor-icons/react";
import AiScopeDialog from "../quotes/AiScopeDialog";
import CaveatLibraryPicker, { appendCaveat } from "./CaveatLibraryPicker";
import SaveLineMenu, { saveLineHandler } from "./SaveLineMenu";
import ScopeSetPicker from "./ScopeSets";

export const scopeLines = (text) => (text || "").split("\n").map((l) => l.trim()).filter(Boolean);

/* Read-only rendering shared by admin cards and the client portal: "-" lines become bullets. */
export function ScopeText({ text, className = "", testid }) {
  const items = scopeLines(text);
  if (!items.length) return null;
  return (
    <div className={`text-sm space-y-1 ${className}`} data-testid={testid}>
      {items.map((l, i) => (/^[-*•]/.test(l)
        ? <p key={i} className="flex gap-2"><span className="text-muted-foreground">•</span><span>{l.replace(/^[-*•]\s*/, "")}</span></p>
        : <p key={i}>{l}</p>))}
    </div>
  );
}

const PLACEHOLDER = "Describe exactly what the works include, e.g.\n- Isolate supply and remove the failed combi boiler\n- Supply and fit like-for-like replacement with new flue and magnetic filter\n- Commission, gas safety certificate and remove old unit\n- Make good and leave site clean";
const CAVEATS_PLACEHOLDER = "Exclusions and assumptions, e.g.\n- Excludes redecoration and making good beyond the immediate work area\n- Assumes clear access and parking within 50 m\n- Subject to an asbestos survey where required";

/* Caveats & exclusions textarea + standard-caveat pick-list + AI draft (same dialog the quote builder uses). Mirrors ScopeOfWorksField so the two sit together. */
export function CaveatsField({ value, onChange, scope = "", context = {}, rows = 4, testid = "caveats", label = "Caveats & exclusions", hint = "Pulled into quotes built from this record, shown to the client and printed on works orders. Start a line with \"-\" for a bullet. Right-click a line to save it as a standard caveat." }) {
  const [aiOpen, setAiOpen] = useState(false);
  const [menu, setMenu] = useState(null);
  return (
    <div data-testid={`${testid}-field`}>
      <div className={`flex items-start ${label || hint ? "justify-between" : "justify-end"} gap-3 mb-1`}>
        {(label || hint) && (
          <div>
            {label && <p className="text-sm font-medium">{label}</p>}
            {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
          </div>
        )}
        <div className="flex items-center gap-1.5 shrink-0">
          <CaveatLibraryPicker value={value} onPick={(t) => onChange(appendCaveat(value, t))} category={context.category || "general"} testid={`${testid}-library`} />
          <Button type="button" size="sm" variant="outline" className="h-7 text-xs shrink-0 border-indigo-300 text-indigo-700 hover:bg-indigo-50 dark:border-indigo-700 dark:text-indigo-300 dark:hover:bg-indigo-950/40" onClick={() => setAiOpen(true)} data-testid={`${testid}-ai-btn`}>
            <Sparkle size={13} weight="fill" className="mr-1" /> {value?.trim() ? "AI refine" : "AI draft"}
          </Button>
        </div>
      </div>
      <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={rows} placeholder={CAVEATS_PLACEHOLDER} className="text-xs" data-testid={testid} onContextMenu={saveLineHandler(value, setMenu)} />
      <SaveLineMenu kind="caveat" menu={menu} category={context.category || "general"} onClose={() => setMenu(null)} />
      <AiScopeDialog key={aiOpen ? "open" : "closed"} open={aiOpen} onOpenChange={setAiOpen} kind="caveats" entity={context.entity || "job"}
        quote={{ title: context.title || "", description: context.description || "", client_name: context.client_name || "", property_address: context.property_address || "", lines: [] }}
        existingScope={scope || ""} existingCaveats={value || ""} noCaveats
        onApply={({ text, mode }) => onChange(mode === "append" && value?.trim() ? `${value.trimEnd()}\n${text}` : text)} />
    </div>
  );
}

/* Textarea + AI draft used in the Job / Job Request create forms and the edit cards. `context` feeds the AI (title, property, client). */
/* `sets` (optional) = { caveats, notice, onApply({scope_of_works, caveats, notice}) } — enables the one-tap Scope sets picker */
export default function ScopeOfWorksField({ value, onChange, context = {}, rows = 6, testid = "scope-of-works", label = "Scope of Works", sets = null, hint = "Shown to the client in their portal and printed on works orders. Start a line with \"-\" for a bullet. Right-click a line to save it as a standard scope line." }) {
  const [aiOpen, setAiOpen] = useState(false);
  const [menu, setMenu] = useState(null);
  return (
    <div data-testid={`${testid}-field`}>
      <div className={`flex items-start ${label || hint ? "justify-between" : "justify-end"} gap-3 mb-1`}>
        {(label || hint) && (
          <div>
            {label && <p className="text-sm font-medium">{label}</p>}
            {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
          </div>
        )}
        <div className="flex items-center gap-1.5 shrink-0">
          {sets && <ScopeSetPicker scope={value || ""} caveats={sets.caveats || ""} notice={sets.notice || null} category={context.category || "general"} onApply={sets.onApply} testid={`${testid}-sets`} compact />}
          <CaveatLibraryPicker kind="scope" value={value} onPick={(t) => onChange(appendCaveat(value, t))} category={context.category || "general"} testid={`${testid}-library`} />
          <Button type="button" size="sm" variant="outline" className="h-7 text-xs shrink-0 border-indigo-300 text-indigo-700 hover:bg-indigo-50 dark:border-indigo-700 dark:text-indigo-300 dark:hover:bg-indigo-950/40" onClick={() => setAiOpen(true)} data-testid={`${testid}-ai-btn`}>
            <Sparkle size={13} weight="fill" className="mr-1" /> {value?.trim() ? "AI refine" : "AI draft"}
          </Button>
        </div>
      </div>
      <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={rows} placeholder={PLACEHOLDER} className="text-xs" data-testid={testid} onContextMenu={saveLineHandler(value, setMenu)} />
      <SaveLineMenu kind="scope" menu={menu} category={context.category || "general"} onClose={() => setMenu(null)} />
      <AiScopeDialog key={aiOpen ? "open" : "closed"} open={aiOpen} onOpenChange={setAiOpen} kind="scope" entity={context.entity || "job"}
        quote={{ title: context.title || "", description: context.description || "", client_name: context.client_name || "", property_address: context.property_address || "", lines: [] }}
        existingScope={value || ""} noCaveats
        onApply={({ text, mode }) => onChange(mode === "append" && value?.trim() ? `${value.trimEnd()}\n${text}` : text)} />
    </div>
  );
}
